import { Avatar, Col, frosted, Icon, Picture, Row, Tappable, Text, useTheme } from "@rally/ui-kit";
import { useEffect, useState } from "react";
import { say } from "../../localization/say.ts";
import { calledBy } from "../../me/called.ts";
import { useFace } from "../../me/face.ts";
import { fileCard } from "../attachment/file-resource.tsx";
import { drawsInline, glyphFor } from "../attachment/filing.ts";
import { useLinkPreview } from "../attachment/link-preview.ts";
import type { Candidate } from "../composer/mentions.ts";
import { type ListedFile, useOpened } from "../filed.ts";
import { useStack } from "../panel/panels.tsx";
import { type PersonHere, personCard } from "../person/person.tsx";
import { agentCard } from "../run/agent.tsx";
import { faceOf, linkFace, type Piece, piecesOf, type Tag, type Taggable } from "./tags.ts";

const A_MARK_IS = 12;

const LIKE_A_WORD = {
  display: "inline-block",
  whiteSpace: "nowrap",
  verticalAlign: "middle",
} as object;

const BESIDE_THE_NAME = {
  display: "inline-flex",
  verticalAlign: "middle",
  position: "relative",
  top: -1,
} as object;

const A_NAME_FITS_IN = 32;
const shortened = (name: string): string =>
  name.length > A_NAME_FITS_IN ? `${name.slice(0, A_NAME_FITS_IN - 1)}…` : name;

function Face({ who }: { who: Candidate }) {
  const face = useFace(who.avatar);
  const name = calledBy(who) ?? who.handle;
  return (
    <Avatar
      name={name}
      src={face ?? undefined}
      size={A_MARK_IS}
      tone={who.kind === "agent" ? "accent" : "neutral"}
    />
  );
}

function MicroImage({ file }: { file: Taggable }) {
  const theme = useTheme();
  const url = useOpened(file.id, "thumb");
  return url ? (
    <Picture
      source={{ uri: url }}
      testID={`tag-file-thumbnail-${file.id}`}
      resizeMode="cover"
      style={{ width: A_MARK_IS, height: A_MARK_IS, borderRadius: theme.radius.xs }}
    />
  ) : (
    <Icon name="image" size={A_MARK_IS - 1} color="text.tertiary" />
  );
}

function UrlMark({ href, conversation }: { href: string; conversation?: string }) {
  const theme = useTheme();
  const preview = useLinkPreview(conversation, href);
  return preview?.favicon ? (
    <Picture
      source={{ uri: preview.favicon }}
      testID="tag-favicon"
      resizeMode="cover"
      style={{ width: A_MARK_IS, height: A_MARK_IS, borderRadius: theme.radius.xs }}
    />
  ) : (
    <Icon name="globe" size={A_MARK_IS - 1} color="text.tertiary" />
  );
}

function Mark({ tag, conversation }: { tag: Tag; conversation?: string }) {
  if ("who" in tag) return <Face who={tag.who} />;
  if (tag.kind === "url") {
    return (
      <Col style={{ width: A_MARK_IS, height: A_MARK_IS, alignItems: "center", justifyContent: "center" }}>
        <UrlMark href={tag.href} conversation={conversation} />
      </Col>
    );
  }
  if (tag.file && drawsInline(tag.file)) return <MicroImage file={tag.file} />;
  return (
    <Col style={{ width: A_MARK_IS, height: A_MARK_IS, alignItems: "center", justifyContent: "center" }}>
      <Icon name={tag.file ? glyphFor(tag.file) : "file"} size={A_MARK_IS - 1} color="text.tertiary" />
    </Col>
  );
}

function saidAs(tag: Tag): string {
  if (tag.kind === "file") return say(tag.file ? "tag.file" : "tag.fileGone")(faceOf(tag));
  if (tag.kind === "url") return say("tag.url")(faceOf(tag));
  if (tag.kind === "agent") return say("tag.agent")(faceOf(tag));
  return say("tag.person")(faceOf(tag));
}

function Chip({
  tag,
  onPress,
  testID,
  conversation,
}: {
  tag: Tag;
  onPress: () => void;
  testID: string;
  conversation?: string;
}) {
  const theme = useTheme();

  const gone = tag.kind === "file" && tag.file === null;
  const link = tag.kind === "url" ? linkFace(tag.href) : null;
  const somebody = tag.kind === "person" || tag.kind === "agent";
  return (
    <Tappable
      onPress={gone ? undefined : onPress}
      disabled={gone}
      accessibilityRole={gone ? "text" : "button"}
      accessibilityLabel={saidAs(tag)}
      testID={testID}
      {...(tag.kind === "url" ? ({ dataSet: { linkPreviewAnchor: "true" } } as object) : {})}
      style={{
        paddingHorizontal: 5,
        paddingVertical: 1,
        borderRadius: theme.radius.sm,

        backgroundColor: somebody ? theme.color.accentSoft : theme.color["bg.surfaceAlt"],
        opacity: gone ? 0.55 : 1,
        ...(LIKE_A_WORD as object),
      }}
    >
      <Col
        style={{
          marginRight: 4,
          height: A_MARK_IS,
          alignItems: "center",
          justifyContent: "center",
          ...(BESIDE_THE_NAME as object),
        }}
      >
        <Mark tag={tag} conversation={conversation} />
      </Col>
      <Text typeName="body" color={somebody ? "accent" : "text.primary"}>
        {link ? link.host : shortened(faceOf(tag))}
        {link?.tail ? " " : null}
        {link?.tail ? (
          <Text typeName="body" color="text.tertiary">
            {link.tail}
          </Text>
        ) : null}
      </Text>
    </Tappable>
  );
}

function listed(file: Taggable): file is ListedFile {
  const found = file as Partial<ListedFile>;
  return (
    typeof found.at === "string" &&
    typeof found.comments === "number" &&
    typeof found.thumb === "boolean" &&
    Array.isArray(found.reactions)
  );
}

function asPerson(who: Candidate): PersonHere {
  return { ...who, role: who.role ?? "member", removedAt: null };
}

function LinkPreview({ href, conversation }: { href: string; conversation?: string }) {
  const theme = useTheme();
  const preview = useLinkPreview(conversation, href);
  const link = linkFace(href);
  return (
    <Col
      testID="link-preview"
      style={{
        position: "absolute",
        left: 0,
        bottom: "100%",
        marginBottom: theme.space[2],
        width: 320,
        maxWidth: "90%" as never,
        borderRadius: theme.radius.lg,
        borderWidth: 1,
        borderColor: theme.color["border.default"],
        backgroundColor: theme.color["bg.surface"],
        overflow: "hidden",
        zIndex: 4,
        ...theme.elevation.raised,
        ...(frosted(theme, 30) as object),
      }}
    >
      {preview?.image ? (
        <Picture
          source={{ uri: preview.image }}
          testID="link-preview-image"
          resizeMode="cover"
          style={{ width: "100%", height: 150 }}
        />
      ) : null}
      <Col style={{ padding: theme.space[3], gap: theme.space[2] }}>
        <Row gap={2}>
          {preview?.favicon ? (
            <Picture
              source={{ uri: preview.favicon }}
              resizeMode="cover"
              style={{ width: 24, height: 24, borderRadius: theme.radius.xs }}
            />
          ) : (
            <Icon name="globe" size={20} color="text.tertiary" />
          )}
          <Col style={{ flex: 1, minWidth: 0 }}>
            <Text typeName="label" numberOfLines={2}>
              {preview?.title || link.host}
            </Text>
            <Text typeName="caption" color="text.tertiary" numberOfLines={1}>
              {preview?.host || link.host}
            </Text>
          </Col>
        </Row>
        {preview?.description ? (
          <Text typeName="caption" color="text.secondary" numberOfLines={3}>
            {preview.description}
          </Text>
        ) : preview === undefined ? (
          <Text typeName="caption" color="text.tertiary">
            {say("tag.previewing")}
          </Text>
        ) : null}
        <Tappable
          accessibilityRole="link"
          testID="link-follow"
          onPress={() => globalThis.open?.(href, "_blank", "noopener,noreferrer")}
          style={{ alignSelf: "flex-start", paddingVertical: theme.space[1] }}
        >
          <Text typeName="label" color="accent">
            {say("tag.follow")}
          </Text>
        </Tappable>
      </Col>
    </Col>
  );
}

export function dismissesLinkPreview(target: unknown): boolean {
  const closest = (target as { closest?: (selector: string) => unknown } | null)?.closest;
  if (typeof closest !== "function") return true;
  return !closest.call(target, '[data-link-preview="true"], [data-link-preview-anchor="true"]');
}

export function linesOf(pieces: readonly Piece[]): Piece[][] {
  const lines: Piece[][] = [[]];
  for (const piece of pieces) {
    if (piece.tag) {
      lines.at(-1)?.push(piece);
      continue;
    }
    const parts = piece.text.split("\n");
    parts.forEach((part, index) => {
      if (index > 0) lines.push([]);
      if (part) lines.at(-1)?.push({ ...piece, text: part });
    });
  }
  return lines;
}

export function Tagged({
  text,
  who,
  files,
  conversation,
  me,
}: {
  text: string;

  who: readonly Candidate[];

  files: readonly Taggable[];
  conversation?: string;
  me?: string;
}) {
  const stack = useStack();
  const [open, setOpen] = useState<Tag | null>(null);
  const lines = linesOf(piecesOf(text, who, files));
  useEffect(() => {
    if (!open) return;
    const pressed = (event: PointerEvent) => {
      if (dismissesLinkPreview(event.target)) setOpen(null);
    };
    const keyed = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(null);
    };
    document.addEventListener("pointerdown", pressed);
    document.addEventListener("keydown", keyed);
    return () => {
      document.removeEventListener("pointerdown", pressed);
      document.removeEventListener("keydown", keyed);
    };
  }, [open]);

  return (
    <Col testID="said-lines" style={{ position: "relative" }}>
      {lines.map((line, index) => (
        <Col key={index}>
          <Text typeName="body" color="text.secondary">
            {line.map((piece) =>
              piece.tag ? (
                <Text key={piece.at} typeName="body" color="inherit">
                  <Chip
                    tag={piece.tag}
                    conversation={conversation}
                    testID={
                      piece.tag.kind === "person" || piece.tag.kind === "agent"
                        ? `mention-in-line-${piece.tag.who.handle}`
                        : `tag-${piece.tag.kind}-${piece.at}`
                    }
                    onPress={() => {
                      const tag = piece.tag;
                      if (!tag) return;
                      if (tag.kind === "url") {
                        setOpen((was) => (was === tag ? null : tag));
                        return;
                      }
                      if (tag.kind === "file" && tag.file && listed(tag.file) && conversation) {
                        stack.open(fileCard(tag.file, conversation));
                        return;
                      }
                      if ((tag.kind === "person" || tag.kind === "agent") && conversation && me) {
                        const person = asPerson(tag.who);
                        stack.open(
                          tag.kind === "agent"
                            ? agentCard(conversation, me, person)
                            : personCard(conversation, me, person),
                        );
                      }
                    }}
                  />
                  {piece.spaceAfter ? " " : null}
                </Text>
              ) : (
                <Text key={piece.at} typeName="body" color="inherit">
                  {piece.text}
                </Text>
              ),
            )}
          </Text>
        </Col>
      ))}
      {open?.kind === "url" ? (
        <Col {...({ dataSet: { linkPreview: "true" } } as object)}>
          <LinkPreview href={open.href} conversation={conversation} />
        </Col>
      ) : null}
    </Col>
  );
}
